import {
  AuthorizationChannel,
  type Consent,
  type ConsentType,
  REFERRAL_CODE_PATTERN,
  UserConvertTemporaryUserFailureReason,
  UserRequestCodeFailureMessage,
} from '@repo/domain';
import {
  BusinessValidationCode,
  BusinessValidationError,
  useBillingActions,
  useConsentActions,
  useUserActions,
} from '@repo/domain-service';
import { useCallback, useEffect, useState } from 'react';

import { selectStatus, useAuthStore } from '@features/auth/stores/useAuthStore';
import { useAuthFormStore } from '@features/auth/stores/useAuthFormStore';
import { useResendCooldown } from '@features/auth/hooks/useResendCooldown';
import { buildPasswordResetUrl } from '@features/auth/utils/buildPasswordResetUrl';

import { useLanguageStore } from '@shared/stores/useLanguageStore';
import { useReservationConsentStore } from '@shared/stores/useReservationConsentStore';
import { isValidEmail } from '@shared/utils/isValidEmail';
import { clearStoredReferralCode, getStoredReferralCode } from '@shared/utils/referralCodeStorage';
import { resolveLanguage } from '@shared/utils/resolveLanguage';

export type AuthModalStep = 'email' | 'code' | 'password' | 'consents';

export interface AuthModalRegistrationData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export const AuthModalError = {
  InvalidEmail: 'InvalidEmail',
  RequestCodeFailed: 'RequestCodeFailed',
  InvalidCode: 'InvalidCode',
  InvalidPassword: 'InvalidPassword',
  ResendCooldownActive: 'ResendCooldownActive',
} as const;

export type AuthModalError = (typeof AuthModalError)[keyof typeof AuthModalError];

export class AuthModalException extends Error {
  constructor(
    public readonly type: AuthModalError,
    public readonly context?: Record<string, string | number>
  ) {
    super(type);
  }
}

export function useAuthModal(registrationData?: AuthModalRegistrationData, initialEmail?: string) {
  const {
    requestCode,
    validateCode,
    validatePassword,
    finalizeCode,
    requestPasswordReset: requestPasswordResetAction,
    registerAsGuest,
    convertTemporaryUser,
    getMe,
    updateUser,
    applyReferralCode,
  } = useUserActions();
  const { getBillings, updateBilling } = useBillingActions();
  const { getConsents, acceptConsent } = useConsentActions();
  const storedLanguage = useLanguageStore((state) => state.language);
  const language = resolveLanguage(undefined, storedLanguage);

  const [expectedConsents, setExpectedConsents] = useState<Consent[]>([]);
  const [conversionConflictEmail, setConversionConflictEmail] = useState<string | undefined>(undefined);

  const email = useAuthFormStore((state) => state.email);
  const cooldownEmail = useAuthFormStore((state) => state.cooldownEmail);
  const resendAvailableAt = useAuthFormStore((state) => state.resendAvailableAt);
  const setCooldown = useAuthFormStore((state) => state.setCooldown);
  const resolveIdempotencyKey = useAuthFormStore((state) => state.resolveIdempotencyKey);
  const clearIdempotencyKey = useAuthFormStore((state) => state.clearIdempotencyKey);
  const resetAuthForm = useAuthFormStore((state) => state.reset);

  const setPendingConsents = useAuthStore((state) => state.setPendingConsents);
  const setConsentsNeeded = useAuthStore((state) => state.setConsentsNeeded);

  const [step, setStep] = useState<AuthModalStep>(() => {
    const auth = useAuthStore.getState();
    if (auth.pendingConsents || auth.consentsNeeded) return 'consents';

    const persisted = useAuthFormStore.getState();
    const cooldownActive =
      !!persisted.resendAvailableAt && persisted.resendAvailableAt > Date.now() && !!persisted.cooldownEmail;
    if (!cooldownActive) return 'email';
    if (initialEmail && initialEmail.toLowerCase() !== persisted.cooldownEmail.toLowerCase()) return 'email';
    return 'code';
  });
  const resendSecondsLeft = useResendCooldown(resendAvailableAt);

  useEffect(() => {
    if (step !== 'consents') return;
    if (expectedConsents.length > 0) return;

    let cancelled = false;
    getConsents({ context: 'userAccount' })
      .then((consents) => {
        if (cancelled) return;
        const expected = consents.filter((consent) => consent.isExpected);
        if (expected.length > 0) {
          setExpectedConsents(expected);
        } else {
          setConsentsNeeded(false);
          setPendingConsents(false);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [step, expectedConsents.length, getConsents, setConsentsNeeded, setPendingConsents]);

  const runRequestCode = useCallback(
    async (targetEmail: string): Promise<void> => {
      const idempotencyKey = resolveIdempotencyKey();
      const response = await requestCode({
        channel: AuthorizationChannel.Email,
        identifier: targetEmail,
        language,
        idempotencyKey,
      });

      if (!response.success) {
        if (response.message !== UserRequestCodeFailureMessage.PasswordLoginRequired) {
          throw new AuthModalException(AuthModalError.RequestCodeFailed);
        }

        // The account logs in with a password, so no code was sent — ask for the password instead.
        // The key is dropped too, so a later request for the same email is not answered from cache.
        clearIdempotencyKey();
        setStep('password');
        return;
      }

      clearIdempotencyKey();
      setCooldown(response.resendAvailableAt, targetEmail);
      setStep('code');
    },
    [requestCode, language, setCooldown, resolveIdempotencyKey, clearIdempotencyKey]
  );

  const submitEmail = useCallback(
    async (emailOverride?: string): Promise<void> => {
      const currentEmail = emailOverride ?? useAuthFormStore.getState().email;
      if (!currentEmail.trim()) return;
      if (!isValidEmail(currentEmail)) throw new AuthModalException(AuthModalError.InvalidEmail);

      if (resendSecondsLeft > 0 && currentEmail.toLowerCase() === cooldownEmail.toLowerCase()) {
        throw new AuthModalException(AuthModalError.ResendCooldownActive, {
          email: currentEmail,
          seconds: resendSecondsLeft,
        });
      }

      const authState = useAuthStore.getState();
      const isGuest = selectStatus(authState) === 'guest';

      if (!isGuest) {
        return runRequestCode(currentEmail);
      }

      const previousConversionEmail = authState.userConversionEmail;
      if (previousConversionEmail && previousConversionEmail.toLowerCase() === currentEmail.toLowerCase()) {
        return runRequestCode(currentEmail);
      }

      authState.setUserConversionEmail(currentEmail);
      const conversion = await convertTemporaryUser({ email: currentEmail });

      if (conversion.success) {
        return runRequestCode(currentEmail);
      }

      if (conversion.reason === UserConvertTemporaryUserFailureReason.UserIsNotTemporary) {
        setConversionConflictEmail(currentEmail);
        return;
      }

      throw new AuthModalException(AuthModalError.RequestCodeFailed);
    },
    [resendSecondsLeft, cooldownEmail, convertTemporaryUser, runRequestCode]
  );

  const completeLogin = useCallback(
    async (isNewUser: boolean): Promise<void> => {
      // Set pendingConsents before finalizeCode so AuthGuard and other mount points
      // keep the modal open while the status flips from guest to authenticated.
      // We clear it below if no consents are expected or the flow fails.
      setPendingConsents(true);

      try {
        await finalizeCode();
        useAuthStore.getState().setUserConversionEmail(undefined);

        if (isNewUser) {
          const storedReferralCode = getStoredReferralCode();
          if (storedReferralCode) {
            if (REFERRAL_CODE_PATTERN.test(storedReferralCode)) {
              // Best-effort: never block registration. Clear the stored code only on a
              // confirmed success, so a transient failure leaves it to retry on a later
              // registration instead of silently discarding the referral.
              const referralResult = await applyReferralCode({ code: storedReferralCode }).catch(() => undefined);
              if (referralResult?.success) {
                clearStoredReferralCode();
              }
            } else {
              // The code never passed through the masked input — it came from a URL and
              // may predate the capture-time guard. A shape mismatch is deterministic, so
              // retrying can never succeed; drop it instead of re-sending on every signup.
              clearStoredReferralCode();
            }
          }
        }

        // Best-effort, in parallel: remember the reservation consents for this account
        // so the reservation flow does not have to fetch them again.
        const ownerId = useAuthStore.getState().user?.id;
        const [consents] = await Promise.all([
          getConsents({ context: 'userAccount' }),
          getConsents({ context: 'reservation' })
            .then((reservationConsents) =>
              useReservationConsentStore.getState().saveCheck(ownerId, reservationConsents)
            )
            .catch(() => {}),
        ]);
        const expected = consents.filter((consent) => consent.isExpected);

        if (expected.length > 0) {
          setExpectedConsents(expected);
          setConsentsNeeded(true);
          setStep('consents');
        } else {
          setPendingConsents(false);
          setConsentsNeeded(false);
          resetAuthForm();
        }
      } catch (error) {
        setPendingConsents(false);
        throw error;
      }
    },
    [finalizeCode, getConsents, applyReferralCode, setPendingConsents, setConsentsNeeded, resetAuthForm]
  );

  const confirmCode = useCallback(
    async (code: string): Promise<void> => {
      const { isNewUser } = await validateCode({ code, language }).catch((error: unknown) => {
        if (
          error instanceof BusinessValidationError &&
          error.code === BusinessValidationCode.authorizationCodeDoesNotExist
        ) {
          throw new AuthModalException(AuthModalError.InvalidCode);
        }
        throw error;
      });

      await completeLogin(isNewUser);
    },
    [validateCode, language, completeLogin]
  );

  const confirmPassword = useCallback(
    async (password: string): Promise<void> => {
      const currentEmail = useAuthFormStore.getState().email;

      await validatePassword({ email: currentEmail, password }).catch((error: unknown) => {
        if (error instanceof BusinessValidationError && error.code === BusinessValidationCode.invalidLoginCredentials) {
          throw new AuthModalException(AuthModalError.InvalidPassword);
        }
        throw error;
      });

      // Only an existing account can log in with a password.
      await completeLogin(false);
    },
    [validatePassword, completeLogin]
  );

  const requestPasswordReset = useCallback(async (): Promise<void> => {
    await requestPasswordResetAction({
      email: useAuthFormStore.getState().email,
      resetPasswordUrl: buildPasswordResetUrl(window.location.origin),
      idempotencyKey: crypto.randomUUID(),
    });
  }, [requestPasswordResetAction]);

  const resendCode = useCallback(async (): Promise<void> => {
    if (resendSecondsLeft > 0) return;

    const authState = useAuthStore.getState();
    const isGuest = selectStatus(authState) === 'guest';

    if (!isGuest) {
      await runRequestCode(email);
      return;
    }

    const previousConversionEmail = authState.userConversionEmail;
    if (previousConversionEmail && previousConversionEmail.toLowerCase() === email.toLowerCase()) {
      await runRequestCode(email);
      return;
    }

    authState.setUserConversionEmail(email);
    const conversion = await convertTemporaryUser({ email });

    if (conversion.success) {
      await runRequestCode(email);
      return;
    }

    if (conversion.reason === UserConvertTemporaryUserFailureReason.UserIsNotTemporary) {
      setConversionConflictEmail(email);
      return;
    }

    throw new AuthModalException(AuthModalError.RequestCodeFailed);
  }, [resendSecondsLeft, convertTemporaryUser, email, runRequestCode]);

  const confirmConversionConflict = useCallback(async (): Promise<void> => {
    const targetEmail = conversionConflictEmail;
    setConversionConflictEmail(undefined);
    if (!targetEmail) return;
    await runRequestCode(targetEmail);
  }, [conversionConflictEmail, runRequestCode]);

  const cancelConversionConflict = useCallback((): void => {
    setConversionConflictEmail(undefined);
    useAuthStore.getState().setUserConversionEmail(undefined);
  }, []);

  const changeEmail = useCallback(() => {
    setStep('email');
  }, []);

  const continueAsGuest = useCallback(async (): Promise<void> => {
    // Keep the modal open while we flip status to 'guest' so that AuthGuard /
    // AuthOverlay don't briefly let the user through before we know whether
    // consents are needed.
    setPendingConsents(true);

    try {
      await registerAsGuest();

      const consents = await getConsents({ context: 'userAccount' });
      const expected = consents.filter((consent) => consent.isExpected);

      if (expected.length > 0) {
        setExpectedConsents(expected);
        setConsentsNeeded(true);
        setStep('consents');
      } else {
        setPendingConsents(false);
        setConsentsNeeded(false);
        resetAuthForm();
      }
    } catch (error) {
      setPendingConsents(false);
      throw error;
    }
  }, [registerAsGuest, getConsents, setPendingConsents, setConsentsNeeded, resetAuthForm]);

  // Since there is no backend method to register user - those three calls are made
  // te replace missing backend solution.
  const runAdditionalRegistration = useCallback(
    async (user: { id: string; email: string }, data: AuthModalRegistrationData) => {
      const fullName = `${data.firstName} ${data.lastName}`;

      await updateUser({
        id: user.id,
        name: fullName,
        phone: data.phoneNumber,
        language,
      });

      const billings = await getBillings();
      const defaultBilling = billings.find((billing) => billing.isDefault);
      if (defaultBilling) {
        await updateBilling({
          id: defaultBilling.id,
          entityType: defaultBilling.entityType,
          name: fullName,
          invoiceEmail: user.email,
          isMonthly: defaultBilling.isMonthly,
          isInvoiceRequested: defaultBilling.isInvoiceRequested,
        });
      }
    },
    [updateUser, getBillings, updateBilling, language]
  );

  const acceptConsents = useCallback(
    async (acceptedTypes: ConsentType[]) => {
      const requiredTypes = expectedConsents.filter((consent) => consent.isRequired).map((consent) => consent.type);
      const allRequiredAccepted = requiredTypes.every((type) => acceptedTypes.includes(type));
      if (!allRequiredAccepted) return;

      const targets = expectedConsents.filter((consent) => acceptedTypes.includes(consent.type));
      const results = await Promise.allSettled(
        targets.map((consent) =>
          acceptConsent({
            context: 'userAccount',
            type: consent.type,
            version: consent.currentVersion,
          })
        )
      );

      const firstRejection = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (firstRejection) {
        // Some consents may have been accepted server-side. Refresh from the
        // server so the user only sees what's still outstanding on retry.
        const refreshed = await getConsents({ context: 'userAccount' }).catch(() => null);
        if (refreshed) {
          setExpectedConsents(refreshed.filter((consent) => consent.isExpected));
        }
        throw firstRejection.reason;
      }

      if (registrationData) {
        const user = await getMe();
        if (user) {
          await runAdditionalRegistration(user, registrationData).catch(() => {});
        }
      }

      setPendingConsents(false);
      setConsentsNeeded(false);
      setExpectedConsents([]);
      resetAuthForm();
    },
    [
      expectedConsents,
      acceptConsent,
      getMe,
      getConsents,
      runAdditionalRegistration,
      registrationData,
      setPendingConsents,
      setConsentsNeeded,
      resetAuthForm,
    ]
  );

  return {
    step,
    expectedConsents,
    resendSecondsLeft,
    submitEmail,
    confirmCode,
    confirmPassword,
    requestPasswordReset,
    resendCode,
    changeEmail,
    continueAsGuest,
    acceptConsents,
    conversionConflictOpen: conversionConflictEmail !== undefined,
    confirmConversionConflict,
    cancelConversionConflict,
  };
}
